import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Popper from "@material-ui/core/Popper";
import Tooltip from "@material-ui/core/Tooltip";
import FlagOutlinedIcon from "@material-ui/icons/FlagOutlined";
import React, { useMemo, useRef, useState } from "react";
import { CcIconEdit, CcIconMore, CcIconTrash } from "../../icons";
import CcAppointmentChip from "./CcAppointmentChip";
import { CcIconButton } from "../mui";
import CcDeviceTags from "./CcDeviceTags";
import { getResidentCardBarthelBadge } from "./residentSummaryDisplay";
import {
  buildAttentionItems,
  buildWellbeingSignals,
  isResidentAllGood,
  resolvePersonState,
} from "./residentWellbeingDisplay";
import { cx } from "../utils";
import { formatResidentLabel } from "../../../../utils/residentDisplay";

const STATUS_TONE_MAP = {
  success: "success",
  warn: "warning",
  danger: "danger",
  "danger-dark": "danger",
  prospecting: "neutral",
  assessment: "pending",
  closed: "inactive",
};

export const mapResidentStatusTone = (chipColor) => {
  if (!chipColor) return "neutral";
  const key = String(chipColor).trim().toLowerCase();
  return STATUS_TONE_MAP[key] || key;
};

const formatResidentDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const TITLE_LABELS = {
  mr: "Mr",
  mrs: "Mrs",
  ms: "Ms",
  other: "Other",
};

const capitalizeWords = (value) => {
  if (value == null || value === "") return "";
  return String(value)
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatTitle = (title) => {
  if (!title || title === "''") return null;
  const key = String(title).trim().toLowerCase();
  return TITLE_LABELS[key] ?? capitalizeWords(title);
};

const formatGender = (gender) => capitalizeWords(gender) || null;

const computeAge = (dateStr) => {
  if (!dateStr) return null;
  const dob = new Date(dateStr);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    now.getMonth() < dob.getMonth() ||
    (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

const buildResidentIdentity = (resident) => {
  return {
    name: formatResidentLabel(resident, { withRoom: true }),
    dateOfBirth: formatResidentDate(resident?.date_of_birth),
    age: computeAge(resident?.date_of_birth),
    sex: formatGender(resident?.gender),
  };
};

const resolveAvatarSrc = (resident) =>
  resident?.profile_picture || resident?.photo_url || null;

const resolveAvatarInitials = (resident) => {
  const first = resident?.first_name?.[0];
  const last = resident?.last_name?.[0];
  if (first || last) {
    return `${first || ""}${last || ""}`.toUpperCase();
  }
  const parts = String(resident?.name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  return "R";
};

/**
 * Shared circular resident photo / initials avatar (listing cards, kitchen
 * pull list, etc.). Accepts `profile_picture` or meal-pull `photo_url`, and
 * initials from first/last name or a full `name` string.
 */
export const ResidentAvatar = ({ resident, className, size }) => {
  const src = resolveAvatarSrc(resident);
  const initials = resolveAvatarInitials(resident);
  const hasNamedInitials = Boolean(
    resident?.first_name?.[0] ||
      resident?.last_name?.[0] ||
      String(resident?.name || "").trim(),
  );
  const sizeClass = size === "sm" ? "cc-resident-card__avatar--sm" : undefined;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cx("cc-resident-card__avatar", sizeClass, className)}
      />
    );
  }

  return (
    <div
      className={cx(
        "cc-resident-card__avatar",
        sizeClass,
        !hasNamedInitials && "cc-resident-card__avatar--placeholder",
        className,
      )}
      aria-hidden
    >
      {hasNamedInitials ? (
        initials
      ) : (
        <svg
          className="cc-resident-card__avatar-icon"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      )}
    </div>
  );
};

const ResidentCardMenu = ({ onEdit, onDelete, hideEdit }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const stop = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleAction = (action) => (event) => {
    stop(event);
    setOpen(false);
    action?.();
  };

  return (
    <>
      <div
        ref={anchorRef}
        className="cc-resident-card__menu-anchor"
        onClick={stop}
      >
        <CcIconButton
          label="Resident actions"
          variant="secondary"
          size="sm"
          onClick={(event) => {
            stop(event);
            setOpen((prev) => !prev);
          }}
        >
          <CcIconMore size={16} />
        </CcIconButton>
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        style={{ zIndex: 1300 }}
        modifiers={{
          offset: { enabled: true, offset: "0, 6" },
          preventOverflow: {
            enabled: true,
            boundariesElement: "viewport",
          },
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <div>
              <ClickAwayListener onClickAway={handleClose}>
                <div
                  className="cc-resident-card__menu cc-resident-card__menu--popper"
                  role="menu"
                >
                  {!hideEdit ? (
                    <button
                      type="button"
                      className="cc-resident-card__menu-item"
                      role="menuitem"
                      onClick={handleAction(onEdit)}
                    >
                      <CcIconEdit size={16} />
                      Edit
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="cc-resident-card__menu-item cc-resident-card__menu-item--danger"
                    role="menuitem"
                    onClick={handleAction(onDelete)}
                  >
                    <CcIconTrash size={16} />
                    Delete
                  </button>
                </div>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </>
  );
};

/** How many safety flags show before folding into "+N". */
const MAX_VISIBLE_FLAGS = 4;

/** Flag display priority — danger/warning safety flags before profile facts. */
const FLAG_TONE_RANK = { danger: 0, warning: 1, info: 2, neutral: 3 };

/**
 * CcResidentCard — resident tile for listing grids ("calm card" layout).
 *
 * Device tags (catheter / stoma / PEG — card_summary.devices) sit directly
 * under the name/meta, always visible and never folded into "+N"; the flag row
 * skips any `kind: "device"` entries so they are not repeated there.
 *
 * Identity row = 80px portrait tile beside name, age · gender and the
 * wellbeing line (latest mood / meal / fluids — only when the API ships
 * card_summary.wellbeing), so the bigger photo costs no card height. Below it:
 * attention chips (open AI alerts, overdue observations / evaluations — each
 * deep-links via onOpenTab) and a one-line safety-flag list (`flags` prop, +N
 * overflow, Barthel only when scored). Fixed shape — no expandable summary.
 */
const CcResidentCard = ({
  resident,
  onClick,
  onEdit,
  onDelete,
  /**
   * Open a specific resident-edit tab (slug) — used by attention chips.
   * Called as onOpenTab(slug, item); the flag chip's item carries
   * `page: "flags"` (it belongs on the Flags page, not a resident tab).
   */
  onOpenTab = null,
  /** [{key, label, tone, detail}] — e.g. getCriticalStatuses(card_summary). */
  flags = [],
  hideEdit = false,
  hideMenu = false,
  className,
}) => {
  const identity = useMemo(() => buildResidentIdentity(resident), [resident]);
  const cardSummary = resident?.card_summary;
  const barthelBadge = useMemo(
    () =>
      cardSummary?.barthel_badge?.score != null
        ? getResidentCardBarthelBadge(resident)
        : null,
    [resident, cardSummary],
  );
  const wellbeingSignals = useMemo(
    () => buildWellbeingSignals(cardSummary),
    [cardSummary],
  );
  const attentionItems = useMemo(
    () => buildAttentionItems(cardSummary),
    [cardSummary],
  );
  const allGood = isResidentAllGood(cardSummary);
  // The status dot reflects the person's state (wellbeing + acute clinical),
  // not pending tasks — those live on the attention chips below.
  const statusMeta = resolvePersonState(cardSummary);
  // Severity-tiered so hard-safety flags (danger/warning: DNACPR, Risk,
  // Allergies, DoLS) rise above static profile counts before truncation, and
  // every safety flag stays visible even past MAX_VISIBLE_FLAGS — only the
  // info/neutral profile facts (ReSPECT, Abilities, Equipment, Supports) fold
  // into "+N". A safety flag must never be reachable only via hover. Array.sort
  // is stable, so clinical order within a tier is preserved. Device entries
  // (getCriticalStatuses adds them for the header) have their own line above.
  const orderedFlags = flags
    .filter((flag) => flag.kind !== "device")
    .sort(
      (a, b) => (FLAG_TONE_RANK[a.tone] ?? 3) - (FLAG_TONE_RANK[b.tone] ?? 3),
    );
  const safetyFlagCount = orderedFlags.filter(
    (flag) => flag.tone === "danger" || flag.tone === "warning",
  ).length;
  const flagCutoff = Math.max(MAX_VISIBLE_FLAGS, safetyFlagCount);
  const visibleFlags = orderedFlags.slice(0, flagCutoff);
  const overflowFlags = orderedFlags.slice(flagCutoff);

  const metaLine = [
    identity.age != null ? `${identity.age}` : null,
    identity.sex,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleCardClick = (event) => {
    onClick?.(event);
  };

  const handleKeyDown = (event) => {
    // Keydown bubbles from the nested attention buttons / kebab menu —
    // intercepting it would cancel their native Enter/Space activation and
    // misnavigate to the card default.
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.(event);
    }
  };

  const handleAttentionClick = (item) => (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (onOpenTab) {
      onOpenTab(item.tab, item);
    } else {
      onClick?.(event);
    }
  };

  return (
    <article
      className={cx("cc-resident-card", className)}
      onClick={handleCardClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="cc-resident-card__body">
        <div className="cc-resident-card__identity">
          <div className="cc-resident-card__avatar-wrap">
            <ResidentAvatar
              resident={resident}
              className="cc-resident-card__avatar--tile"
            />
          </div>
          <div className="cc-resident-card__identity-copy">
            <div className="cc-resident-card__title-row">
              <div className="cc-resident-card__title-main">
                {statusMeta ? (
                  <span
                    className="cc-resident-card__status"
                    role="img"
                    title={statusMeta.label}
                    aria-label={statusMeta.label}
                  >
                    {statusMeta.emoji}
                  </span>
                ) : null}
                {identity.name ? (
                  <p className="cc-resident-card__name">{identity.name}</p>
                ) : null}
              </div>
              {!hideMenu && onDelete ? (
                <ResidentCardMenu
                  onEdit={onEdit ?? onClick}
                  onDelete={onDelete}
                  hideEdit={hideEdit}
                />
              ) : null}
            </div>
            {metaLine || identity.dateOfBirth ? (
              <p
                className="cc-resident-card__meta"
                title={
                  identity.dateOfBirth
                    ? `Born ${identity.dateOfBirth}`
                    : undefined
                }
              >
                {metaLine || identity.dateOfBirth}
              </p>
            ) : null}
            {wellbeingSignals ? (
              <div className="cc-resident-card__wellbeing">
                {wellbeingSignals.length > 0 ? (
                  wellbeingSignals.map((signal) => (
                    <Tooltip
                      key={signal.key}
                      title={signal.title || ""}
                      arrow
                      placement="top"
                    >
                      <span
                        className={cx(
                          "cc-resident-card__signal",
                          signal.tone &&
                            `cc-resident-card__signal--${signal.tone}`,
                        )}
                        aria-label={signal.title || undefined}
                      >
                        <span
                          className="cc-resident-card__signal-emoji"
                          aria-hidden
                        >
                          {signal.emoji}
                        </span>
                        <span className="cc-resident-card__signal-label">
                          {signal.label}
                        </span>
                        {signal.detail ? (
                          <span className="cc-resident-card__signal-detail">
                            {signal.detail}
                          </span>
                        ) : null}
                      </span>
                    </Tooltip>
                  ))
                ) : (
                  <span className="cc-resident-card__wellbeing-empty">
                    No recent mood
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>

        <CcDeviceTags devices={cardSummary?.devices} />

        <CcAppointmentChip
          appointments={cardSummary?.appointments}
          onOpen={(event) =>
            onOpenTab ? onOpenTab("appointments") : onClick?.(event)
          }
        />

        {attentionItems.length > 0 ? (
          <div className="cc-resident-card__attention">
            {attentionItems.map((item) => (
              <button
                key={item.key}
                type="button"
                title={item.title || undefined}
                className={cx(
                  "cc-resident-card__chip",
                  `cc-resident-card__chip--${item.tone}`,
                )}
                onClick={handleAttentionClick(item)}
              >
                {item.icon === "flag" ? (
                  <FlagOutlinedIcon
                    className="cc-resident-card__chip-icon"
                    aria-hidden
                  />
                ) : null}
                {item.label}
                {item.sub ? (
                  <span className="cc-resident-card__chip-sub">
                    {` · ${item.sub}`}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : allGood ? (
          <div className="cc-resident-card__allgood">
            <span aria-hidden>✓</span> All on track
          </div>
        ) : null}

        {visibleFlags.length > 0 || barthelBadge ? (
          <div className="cc-resident-card__flags">
            {visibleFlags.map((flag) => (
              <span
                key={flag.key}
                title={flag.detail || undefined}
                className={cx(
                  "cc-resident-card__flag",
                  (flag.tone === "danger" || flag.tone === "warning") &&
                    "cc-resident-card__flag--safety",
                )}
              >
                {flag.label}
              </span>
            ))}
            {overflowFlags.length > 0 ? (
              <span
                className="cc-resident-card__flag cc-resident-card__flag--more"
                title={overflowFlags.map((flag) => flag.label).join(" · ")}
              >
                +{overflowFlags.length}
              </span>
            ) : null}
            {barthelBadge ? (
              <span
                className="cc-resident-card__flag cc-resident-card__flag--score"
                title={barthelBadge.tooltip}
              >
                {barthelBadge.label}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default CcResidentCard;
